import { Task } from 'components/Gantt/CommonGantt/CommonGanttTypes';
import { GridElement } from 'components/scheduler/table/table-utils/tableOrgByTypes';
import { NODE_TYPES } from 'state_management/NODE_TYPES';
import { ElementTreeBasic } from 'state_management/scheduler/schedule';
import { ScheduleElementType } from 'state_management/scheduler/ScheduleElementType';
import { Child, ScheduleElement } from 'state_management/scheduler/scheduler_types';
import { ScheduleElementUIProps } from 'state_management/scheduler/threadedTypes';
import { TIMESPAN_RESOURCE_ELEMENT_TYPE } from 'state_management/threadedConstants';

export const isActivityElement = (elementType: string | undefined): boolean => {
  return elementType === NODE_TYPES.ACTIVITY;
};

export const isTaskElement = (elementType: ScheduleElementType | undefined): boolean => {
  return elementType === NODE_TYPES.TASK;
};

export const isActivityOrTaskElement = (elementType: ScheduleElementType | undefined): boolean => {
  return isActivityElement(elementType) || isTaskElement(elementType);
};

const SET_OF_MILESTONE_NODE_TYPES = new Set([NODE_TYPES.MILESTONE_START, NODE_TYPES.MILESTONE_INTERIM, NODE_TYPES.MILESTONE_END]);
export const isMilestoneElement = (elementType: string | undefined) => {
  return !!elementType && (SET_OF_MILESTONE_NODE_TYPES as Set<string>).has(elementType);
};

export const isParentAnActivity = ({ elementType, parentElementType }: { elementType?: ScheduleElementType; parentElementType?: ScheduleElementType }): boolean => {
  return (isTaskElement(elementType) || elementType === NODE_TYPES.MILESTONE_INTERIM) && isActivityElement(parentElementType);
};

export const isTaskOrInterimMilestone = (elementType: ScheduleElementType | undefined): boolean => {
  return isTaskElement(elementType) || elementType === NODE_TYPES.MILESTONE_INTERIM;
};

export const isNotGroupElement = (elementType: ScheduleElementType | undefined) => {
  return isActivityOrTaskElement(elementType) || isMilestoneElement(elementType);
};

export const isActivityOrMilestoneElement = (elementType: ScheduleElementType | undefined): boolean => {
  return isActivityElement(elementType) || isMilestoneElement(elementType);
};

export const isStartMilestone = (elementType: ScheduleElementType | undefined): boolean => {
  return elementType === NODE_TYPES.MILESTONE_START;
};

export const isInterimMilestone = (elementType: ScheduleElementType | undefined): boolean => {
  return elementType === NODE_TYPES.MILESTONE_INTERIM;
};

export const isEndMilestone = (elementType: ScheduleElementType | undefined): boolean => {
  return elementType === NODE_TYPES.MILESTONE_END;
};

export const isTextBoxElement = (elementType: string | undefined) => {
  return elementType === NODE_TYPES.TEXT_BOX;
};

const SET_OF_CPM_RELEVANT_ELEMENT_TYPES = new Set([NODE_TYPES.MILESTONE_START, NODE_TYPES.MILESTONE_INTERIM, NODE_TYPES.MILESTONE_END, NODE_TYPES.ACTIVITY, NODE_TYPES.GROUP, NODE_TYPES.TASK]);
export const isCPMRelevantElement = (elementType: ScheduleElementType | undefined): boolean => {
  return !!elementType && (SET_OF_CPM_RELEVANT_ELEMENT_TYPES as Set<ScheduleElementType>).has(elementType);
};

export const isGroupElement = (elementType: string | undefined): boolean => {
  return elementType === NODE_TYPES.GROUP;
};

export const isTimespanElement = (elementType: ScheduleElementType | undefined): boolean => {
  return elementType === TIMESPAN_RESOURCE_ELEMENT_TYPE;
};

export const isEmbedImageElement = (elementType: string | undefined): boolean => {
  return elementType === NODE_TYPES.IMAGE;
};

export const isTextBoxOrImageElement = (elementType: ScheduleElementType | undefined): boolean => {
  return isTextBoxElement(elementType) || isEmbedImageElement(elementType);
};

export const isValidElementByType = (elementType: ScheduleElementType | undefined) => {
  return isGroupElement(elementType) || isActivityOrTaskElement(elementType) || isMilestoneElement(elementType);
};

export const isValidElementTypeElement = (element: ScheduleElement | ElementTreeBasic) => {
  return isValidElementByType(element.elementType);
};

export const isVirtualElement = (element?: { scheduleElementUIProps?: ScheduleElementUIProps }): boolean => {
  return !!element?.scheduleElementUIProps?.organizeByGroupData?.type;
};

export const isActivityWithTasks = (data: { elementType?: ScheduleElementType; children?: Child[] } | undefined | null): boolean => {
  return !!(isActivityElement(data?.elementType) && data?.children?.length);
};

export const isElementExpandable = (data: { elementType?: ScheduleElementType; children?: Child[] } | undefined | null): boolean => {
  return isGroupElement(data?.elementType) || isActivityWithTasks(data);
};

export const isEmptyGroupElement = (data: GridElement | Task): boolean => isGroupElement(data?.elementType) && (data?.children?.length ?? 0) === 0;
